import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { AlertTriangle, Loader, Lock } from "lucide-react";
import { useCountries, useUpdateCountry } from "../hooks/useCountrySettings";

const GROUP_LABELS = {
  licensing: "Licensing regime",
  restricted: "Crypto restricted",
  sanctioned: "Sanctioned",
  quidax_prohibited: "Quidax prohibited",
  none: "",
};

function Switch({ checked, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        checked ? "bg-[#013941]" : "bg-gray-300"
      }`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

const FILTERS = [
  { id: "", label: "All" },
  { id: "supported:true", label: "Registration on" },
  { id: "supported:false", label: "Registration off" },
  { id: "crypto:true", label: "Crypto on" },
  { id: "crypto:false", label: "Crypto off" },
];

function CountryTable() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [group, setGroup] = useState("");
  const [key, value] = filter.split(":");
  const { data, isLoading, isError, error } = useCountries({
    search: search || undefined,
    group: group || undefined,
    ...(key ? { [key]: value } : {}),
  });
  const { mutate: update, variables, isPending } = useUpdateCountry();

  const change = (country, payload) =>
    update(
      { uuid: country.uuid, payload },
      {
        onSuccess: () => toast.success(`${country.country} updated`),
        onError: (e) => toast.error(e?.response?.data?.message || "Could not update the country"),
      }
    );

  if (isError) {
    return <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">Error: {error.message}</p>;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center pb-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search country or code"
          className="px-4 py-2 text-sm rounded-3xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-3xl border border-gray-200 outline-none"
        >
          {FILTERS.map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
        <select value={group} onChange={(e) => setGroup(e.target.value)} className="px-3 py-2 text-sm rounded-3xl border border-gray-200 outline-none">
          <option value="">Any list</option>
          {Object.entries(GROUP_LABELS).filter(([id]) => id !== "none").map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
        {data && <span className="text-xs text-gray-500">{data.count} of {data.total}</span>}
      </div>

      {isLoading ? (
        <p className="flex gap-2 items-center font-medium text-gray-600">
          Loading countries <Loader className="w-5 h-5 animate-spin" />
        </p>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="px-4 py-3 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b">Country</th>
                  <th className="px-4 py-3 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b">List</th>
                  <th className="px-4 py-3 text-xs font-bold tracking-wider text-center text-gray-500 uppercase border-b">Registration</th>
                  <th className="px-4 py-3 text-xs font-bold tracking-wider text-center text-gray-500 uppercase border-b">Crypto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.countries.map((c) => {
                  const busy = isPending && variables?.uuid === c.uuid;
                  return (
                    <tr key={c.uuid} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <span className="font-medium">{c.country}</span>
                        <span className="ml-2 text-xs text-gray-400">{c.countryCode}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {GROUP_LABELS[c.group] && (
                          <span
                            className="inline-flex gap-1 items-center px-2 py-0.5 rounded-full bg-gray-100"
                            title={
                              c.quidaxProhibited
                                ? "Quidax prohibits onboarding from this country. Registration can still be allowed here; crypto cannot."
                                : undefined
                            }
                          >
                            {c.quidaxProhibited && <AlertTriangle className="w-3 h-3 text-amber-600" aria-label="Quidax prohibited" />}
                            {GROUP_LABELS[c.group]}
                          </span>
                        )}
                        {c.cryptoLocked && !c.quidaxProhibited && (
                          <span className="ml-1 text-xs text-gray-500">crypto locked</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch
                          label={`Registration for ${c.country}`}
                          checked={c.isSupported}
                          disabled={busy}
                          onChange={(v) => change(c, { isSupported: v })}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex gap-1 items-center" title={c.cryptoLocked && !c.isCryptoLicenseAvailable ? "Crypto cannot be switched on: Quidax-prohibited, sanctioned or crypto-banned" : undefined}>
                          {c.cryptoLocked && !c.isCryptoLicenseAvailable && <Lock className="w-3 h-3 text-gray-400" />}
                          <Switch
                            label={`Crypto for ${c.country}`}
                            checked={c.isCryptoLicenseAvailable}
                            disabled={busy || (c.cryptoLocked && !c.isCryptoLicenseAvailable)}
                            onChange={(v) => change(c, { isCryptoLicenseAvailable: v })}
                          />
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {data?.countries.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-10 text-sm text-center text-gray-500">No countries match.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default CountryTable;
